import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AUTH_ENDPOINTS } from '@/store/api/endpoints';
import { handleApiError } from '@/utils/apiErrorHandler';
import { clearError } from './errorSlice';
import {LOGGED_OUT_KEY} from '@/components/shared/constants';

// Types
export interface User {
  middleName: string | null;
  lastName: string;
  emailVerified: boolean;
  optumUUID: string;
  mpin: number | null;
  pixeUserRelationships?: {
    groupsAdministered: number[];
    physiciansAdministered: number[];
    physiciansDelegated: number[];
  };
  dashboardHideGroupAdminAccess: boolean;
  birthdate: string | null;
  nickname: string;
  phone: string | null;
  sso: string;
  physiciansAdministered: Record<string, boolean> | null;
  status: string;
  username: string;
  optumName: string;
  physiciansDelegated: Record<string, boolean> | null;
  dashboardHidePhysicianAccess: boolean;
  dashboardHideImAPhysician: boolean;
  type: string;
  id: number | null;
  firstName: string;
  groupsAdministered: Record<string, boolean> | null;
  email: string;
  dashboardHideDelegateAccess: boolean;
}

export interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  initialized?: boolean;
  loading: boolean;
  error: string | null;
  showModalLogin: boolean;
  userSearchResults: any | null;
  internalUserCount: number | null;
  externalUserCount: number | null;
}

// Initial state modeled after the old Redux structure
const initialState: UserState = {
  user: null,
  isAuthenticated: false,
  initialized: false,
  loading: false,
  error: null,
  showModalLogin: false,
  userSearchResults: null,
  internalUserCount: null,
  externalUserCount: null
};

// Thunks
export const validateAuth = createAsyncThunk(
  'user/validate',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await fetch(AUTH_ENDPOINTS.VALIDATE);
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      handleApiError(error as any, "user", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to validate user');
    }
  }
);

export const getUserCounts = createAsyncThunk(
  'user/getUserCounts',
  async (type: 'internal' | 'external', { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const endpoint = type === 'internal'
          ? AUTH_ENDPOINTS.USER_COUNTS_INTERNAL
          : AUTH_ENDPOINTS.USER_COUNTS_EXTERNAL;
      const response = await fetch(endpoint);
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      return { type, count: data.count };
    } catch (error) {
      handleApiError(error as any, "user", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue(`Failed to get ${type} user count`);
    }
  }
);
export const logout = createAsyncThunk(
    'user/logout',
    async () => {
      const response = await fetch('/api/auth/logout', { method: 'POST' });
      localStorage.setItem(LOGGED_OUT_KEY, 'true');
      return await response.json();
    }
);
export const setAuthenticatedUser = createAsyncThunk(
  'user/setAuthenticatedUser',
  async (userData: User, { fulfillWithValue }) => {
    // This action just passes the user data through to the reducer
    return fulfillWithValue(userData);
  }
);

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setModalLogin: (state, action: PayloadAction<boolean>) => {
      state.showModalLogin = action.payload;
    },
    clearUserError: (state) => {
      state.error = null;
    },
    resetUser: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // validateAuth
      .addCase(validateAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(validateAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Authentication failed';
        state.isAuthenticated = false;
        state.initialized = true;
      })

      // logoutUser
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Logout failed';
      })

      // getUserCounts
      .addCase(getUserCounts.fulfilled, (state, action) => {
        const { type, count } = action.payload;
        if (type === 'internal') {
          state.internalUserCount = count;
        } else {
          state.externalUserCount = count;
        }
      })

      // setAuthenticatedUser
      .addCase(setAuthenticatedUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
      });
  }
});

export const { setModalLogin, clearUserError, resetUser } = userSlice.actions;

export default userSlice.reducer;
